// Catalog.ID Client — Encrypted Device Slots
// ============================================================================
// A small keyed store of JSON values, encrypted under the DEVICE encryption key
// (registration-wallets §4.12, D12, D19, §7's "Device storage" bullet). It sits
// beside `device-storage.ts`'s key namespace and shares its posture — same DEK,
// same per-identity partition, same sweep by *Forget this device* — but holds
// things that are NOT key-bag material and must never be mistaken for it:
//
//   `bitcash_default_token`              the account's `catalog:default` AUTH
//                                        token (§4.12)
//   `bitcash_org_default_token:<orgCid>` the organization's, one slot each
//                                        (§4.13)
//   `bitcash_voucher_codes`              the codes an organization's owner
//                                        bought and has not handed out (D19)
//
// ── why a slot and not a bag entry ──────────────────────────────────────────
// A token is a CACHE of Worker state, not a secret the account owns: the
// Worker's list is the truth (§10), the token is dropped on any refusal, and a
// refill is one call away. Putting it in the key bag would push it into the
// recovery bundle and onto paper, where a revoked token would live for ever.
// Putting it in the bag would also make every read cost a supersecret.
//
// The voucher codes are the opposite case and the copy must say so: they ARE
// the money (D26 — a lost code is lost bits until the lock expires), the node
// holds no copy, and *Forget this device* deletes them. Print them.
//
// ── why the values are encrypted at all ─────────────────────────────────────
// A default token is a bounded, revocable debit authorisation. Plaintext in
// localStorage would hand it to anything that can read the origin's storage,
// and the DEK is already there for exactly this class of value. The DEK lives
// on the SERVER and reaches this device at unlock, so a stolen browser profile
// alone opens nothing.
//
// ── the ambient identity ────────────────────────────────────────────────────
// Every function defaults to the ACTIVE cid and to the DEK the unlock flow
// last handed in through {@link rememberSlotKey}. That is what lets a consumer
// — the key-grant handler family, say — call `getDeviceSlot('…')` with no
// context at all. When no DEK is known the store is simply CLOSED: reads answer
// null and writes answer false. A cache that cannot be written is not an error.

import * as aes from '../crypto/aes';
import { getActiveCid, isNoLocalStorageMode, slotPrefix } from './device-storage';

/** `{cid}_slot_{name}` — deliberately NOT `{cid}_key_…`, which is the
 *  device-stored key-bag namespace. `hasStoredKeys`, `getStoredKeyEntries` and
 *  `listEnrolledIdentities` all read that one, and a cached token must never
 *  make an unenrolled browser look enrolled. The prefix itself lives in
 *  `device-storage.ts` so that `clearDeviceStorage` sweeps exactly what this
 *  module writes. */
function storageKey(cid: string, name: string): string {
    return `${slotPrefix(cid)}${name}`;
}

// ── well-known slot names ───────────────────────────────────────────────────

/** The account's own default token (§4.12): payee `operator`, empty site and
 *  service-type lists, no expiry, label `catalog:default`. */
export const DEFAULT_TOKEN_SLOT = 'bitcash_default_token';

/** The organization's default token, one slot per organization (§4.13) — an
 *  owner or treasurer may hold several, and they must not overwrite each
 *  other. */
export function orgDefaultTokenSlot(orgCid: string): string {
    return `bitcash_org_default_token:${orgCid}`;
}

/** The voucher and invitation codes an organization's owner bought and has not
 *  handed out (D19). The node stores none of them. */
export const VOUCHER_CODES_SLOT = 'bitcash_voucher_codes';

// ── the device key registry ─────────────────────────────────────────────────

const slotKeys = new Map<string, Uint8Array>();

/** Hand the store the device encryption key for one identity. Called wherever
 *  the DEK already arrives — login, device setup, a registration's device
 *  step — so later callers need no context. */
export function rememberSlotKey(cid: string, dek: Uint8Array): void {
    slotKeys.set(cid, dek);
}

/**
 * The device encryption key this session holds for one identity, or null when
 * it holds none — a locked tab, a supersecret-only session, or a browser the
 * member set up with "no local storage".
 *
 * Read by the on-demand key loader (`key-grant/on-demand-key.ts`,
 * device-setup-restructure §9.1 step 4), which may only offer to KEEP a
 * freshly fetched key on this device when there is a DEK to encrypt it under.
 * No DEK, no offer — and the loader asks nothing rather than asking a question
 * it cannot act on.
 *
 * The bytes are the registry's own, not a copy: callers must neither zero nor
 * mutate them, or every later slot write and key import under this identity
 * would encrypt under a key the server does not hold.
 */
export function getSlotKey(cid: string): Uint8Array | null {
    return slotKeys.get(cid) ?? null;
}

/** Drop one identity's key from memory. The stored values stay on disk and
 *  become unreadable until the next unlock — which is what a locked tab should
 *  look like. */
export function forgetSlotKey(cid: string): void {
    slotKeys.delete(cid);
}

/** Drop every key (logout, tab close, tests). */
export function forgetAllSlotKeys(): void {
    slotKeys.clear();
}

function resolveCid(cid?: string): string | null {
    if (cid) return cid;
    try {
        return getActiveCid();
    } catch {
        return null;
    }
}

function store(): Storage | null {
    try {
        return globalThis.localStorage;
    } catch {
        return null; // privacy modes and non-DOM environments both throw here
    }
}

// ── read / write / clear ────────────────────────────────────────────────────

/**
 * Write a JSON value into one slot. Returns whether it was actually stored:
 * `false` when no identity is active, when no device key is known for it, when
 * the member chose "no local storage" on this device, or when storage refused
 * the write (quota, privacy mode).
 *
 * A caller must NOT treat false as a failure of the operation it was part of —
 * the token was still minted, the codes were still shown. It means only that
 * this device will have to ask again.
 */
export async function setDeviceSlot(
    name: string, value: unknown, cid?: string,
): Promise<boolean> {
    const account = resolveCid(cid);
    if (!account) return false;
    // "No local storage" is a member's explicit choice about this browser; a
    // cache is exactly the kind of convenience it refuses.
    if (isNoLocalStorageMode(account)) return false;
    const dek = slotKeys.get(account);
    if (!dek) return false;
    const s = store();
    if (!s) return false;

    try {
        const nonce = aes.generateNonce();
        const plaintext = aes.encodeUtf8(JSON.stringify(value));
        const ciphertext = await aes.encrypt(dek, nonce, plaintext);
        s.setItem(storageKey(account, name), `${aes.toBase64(nonce)}:${aes.toBase64(ciphertext)}`);
        return true;
    } catch {
        return false;
    }
}

/**
 * Read a slot back, or null when there is nothing readable there.
 *
 * EVERY failure reads as null: an absent slot, a malformed record, a value
 * written under a superseded device key, a storage that throws. This is a
 * cache, and the repair for all four is identical — refill it. Nothing here
 * throws, so no consumer has to wrap a read in a try/catch to stay alive.
 */
export async function getDeviceSlot<T = unknown>(name: string, cid?: string): Promise<T | null> {
    const account = resolveCid(cid);
    if (!account) return null;
    const dek = slotKeys.get(account);
    if (!dek) return null;
    const s = store();
    if (!s) return null;

    const raw = s.getItem(storageKey(account, name));
    if (!raw) return null;
    const [nonceB64, ctB64] = raw.split(':');
    if (!nonceB64 || !ctB64) return null;
    try {
        const plaintext = await aes.decrypt(dek, aes.fromBase64(nonceB64), aes.fromBase64(ctB64));
        return JSON.parse(new TextDecoder().decode(plaintext)) as T;
    } catch {
        return null;
    }
}

/** Forget one slot. Synchronous and total: the record is gone, whatever state
 *  the key registry is in. Called on any Worker refusal of a cached token
 *  (§4.12's cache-invalidation rule) and by the Settings revoke action. */
export function clearDeviceSlot(name: string, cid?: string): void {
    const account = resolveCid(cid);
    if (!account) return;
    store()?.removeItem(storageKey(account, name));
}

/** The slot names one identity holds on this device. Reads the record NAMES
 *  only — no decryption, so it answers with the keys locked. */
export function listDeviceSlots(cid: string): string[] {
    const s = store();
    if (!s) return [];
    const prefix = storageKey(cid, '');
    const names: string[] = [];
    for (let i = 0; i < s.length; i++) {
        const key = s.key(i);
        if (key && key.startsWith(prefix)) names.push(key.slice(prefix.length));
    }
    return names;
}

/** Sweep every slot of one identity. Called by `clearDeviceStorage` — *Forget
 *  this device* must take the cached tokens and the stored voucher codes with
 *  the keys, or a de-enrolled browser would keep spending authority. */
export function clearAllDeviceSlots(cid: string): void {
    const s = store();
    if (!s) return;
    const prefix = storageKey(cid, '');
    const toRemove: string[] = [];
    for (let i = 0; i < s.length; i++) {
        const key = s.key(i);
        if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    for (const key of toRemove) s.removeItem(key);
    slotKeys.delete(cid);
}
